import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { SetStateAction, useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router';

import { FormProvider, StatusTag, TruncatedTypography } from '@app/components';
import { CustomAvatar, CustomAvatarGroup } from '@app/components/custom-avatar';
import { TableCellCustom } from '@app/components/table';
import { RowActions } from './row-action';
import { ILeave } from '../types';
import { useDialog, useRequestState } from '@app/hooks';
import { selectUpdateLeaveStatusState } from '../selectors';
import { updateLeaveStatus } from '../slice';
import { LeaveStatus, Strings } from '@app/constants';
import { useAuthRole } from '@app/utils';
import { ConfirmationDialog, DeleteDialog } from '@app/components/dialog';
import { updateLeaveStatusSchema } from '../validator';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { getEmployees, selectEmployeesState } from '@app/modules/employees';

interface Props {
  row: ILeave;
  selected: boolean;
  onSelectRow: VoidFunction;
  itSelf: boolean;
}

const LeaveRow = (props: Props) => {
  const { row, itSelf } = props;
  const methods = useForm({
    resolver: yupResolver(updateLeaveStatusSchema) as any,
  });
  const dispatch = useDispatch();
  const [request, setRequest] = useState(row.update ? row.update : 'Draft');
  const previousRequestRef = useRef(request);
  const { isSuperAdmin, isManager } = useAuthRole();

  const { data: employees } = useRequestState({
    stateSelector: selectEmployeesState,
  });

  useEffect(() => {
    dispatch(getEmployees());
  }, [dispatch]);

  const filteredUsers =
    employees?.records && employees?.records?.length > 0
      ? employees?.records?.filter((user) => row?.user?.managerId?.includes(String(user.id)))
      : [];

  const handleChange = (event: { target: { value: SetStateAction<string> } }) => {
    const newRequest = event.target.value as string;
    previousRequestRef.current = request;
    setRequest(newRequest);
  };

  const onPressConfirm = (reason?: string) => {
    dispatch(updateLeaveStatus({ ...row, req: request, reason: reason ?? '' }));
  };

  const handleDialogClose = () => {
    setRequest(previousRequestRef.current);
  };

  const deleteDialog = useDialog();
  const confirmDialog = useDialog();

  const { loading } = useRequestState({
    stateSelector: selectUpdateLeaveStatusState,
    onSuccess: deleteDialog.hide,
    successMessageShown: true,
    errorShown: true,
  });

  const { loading: forApproved } = useRequestState({
    stateSelector: selectUpdateLeaveStatusState,
    onSuccess: confirmDialog.hide,
    successMessageShown: true,
    errorShown: true,
  });

  const updatedBy = `${row?.approvedUser?.firstName} ${row?.approvedUser?.lastName}`;
  const userName = `${row?.user.firstName} ${row?.user.lastName}`;
  const userImage = row?.user?.profileImage;

  let statusValue;
  if (row.update === 'Approved') {
    statusValue = LeaveStatus.Approved;
  } else if (row.update === 'Declined') {
    statusValue = LeaveStatus.Declined;
  } else if (row.update === 'Pending') {
    statusValue = LeaveStatus.Pending;
  } else {
    statusValue = LeaveStatus.Draft;
  }

  const navigate = useNavigate();
  const handleDetails = () => {
    navigate(PATH_DASHBOARD.leaveDetail, {
      state: { id: row?.id, row, update: row.update, itSelf },
    });
  };

  return (
    <>
      <TableCellCustom align="center" onClick={handleDetails}>
        <Typography variant="body2">{row.id}</Typography>
      </TableCellCustom>
      {(isManager || isSuperAdmin) && (
        <TableCellCustom flex onClick={handleDetails}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '150px' }}>
            <CustomAvatar name={userName} src={userImage} color="info" />
            <Typography variant="body2" sx={{ ml: 1, flexGrow: 1 }}>
              {userName}
            </Typography>
          </Box>
        </TableCellCustom>
      )}
      {isSuperAdmin && (
        <TableCellCustom align="center" onClick={handleDetails}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '150px',
              justifyContent: 'center',
            }}
          >
            {filteredUsers && filteredUsers?.length > 1 ? (
              <CustomAvatarGroup size="small" max={6}>
                {filteredUsers?.map((user) => (
                  <CustomAvatar
                    key={user.id}
                    name={`${user?.firstName} ${user?.lastName}`}
                    src={user?.profileImage}
                    color="info"
                  />
                ))}
              </CustomAvatarGroup>
            ) : filteredUsers && filteredUsers?.length === 1 ? (
              <Stack direction="row" alignItems="center">
                <CustomAvatar
                  name={`${filteredUsers?.[0]?.firstName} ${filteredUsers?.[0]?.lastName}`}
                  src={filteredUsers?.[0]?.profileImage}
                  color="info"
                />
                <Typography variant="body2" sx={{ ml: 1, flexGrow: 1 }}>
                  {`${filteredUsers?.[0]?.firstName} ${filteredUsers?.[0]?.lastName}`}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </TableCellCustom>
      )}
      <TableCellCustom align="center" onClick={handleDetails}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '120px' }}>
          <Typography variant="body2">{row?.leaveType}</Typography>
        </Box>
      </TableCellCustom>
      <TableCellCustom align="center" onClick={handleDetails}>
        <Typography variant="body2">{row?.from}</Typography>
      </TableCellCustom>
      <TableCellCustom align="center" onClick={handleDetails}>
        <Typography variant="body2">{row?.to}</Typography>
      </TableCellCustom>
      <TableCellCustom align="center" onClick={handleDetails}>
        <TruncatedTypography variant="body2" text={row?.reason} width={120} />
      </TableCellCustom>

      {(isSuperAdmin || (isManager && !itSelf)) && (
        <TableCellCustom>
          <Select
            value={request}
            onChange={handleChange}
            sx={{ height: '25px', fontSize: '13px' }}
            disabled={row?.update === 'Approved' || row?.update === 'Declined'}
          >
            <MenuItem value="Approved" sx={{ fontSize: '13px' }} onClick={deleteDialog.show}>
              {Strings.status.approved}
            </MenuItem>
            <MenuItem value="Declined" sx={{ fontSize: '13px' }} onClick={confirmDialog.show}>
              {Strings.status.declined}
            </MenuItem>
            <MenuItem value="Pending" sx={{ fontSize: '13px' }} onClick={deleteDialog.show}>
              {Strings.status.pending}
            </MenuItem>
          </Select>
        </TableCellCustom>
      )}

      <FormProvider methods={methods}>
        <ConfirmationDialog
          title={Strings.leave.declinedLeave}
          confirmationButtonTitle={Strings.button.save}
          caption={Strings.common.updateCaption(request)}
          loading={forApproved}
          visible={confirmDialog.visible}
          onClickConfirm={onPressConfirm}
          onClose={() => {
            confirmDialog.hide();
            handleDialogClose();
          }}
        />
      </FormProvider>

      <DeleteDialog
        title={`${request} ${Strings.field.leave}`}
        deleteButtonTitle={Strings.button.confirm}
        caption={Strings.common.updateCaption(request)}
        loading={loading}
        visible={deleteDialog.visible}
        onClickDelete={() => {
          onPressConfirm('');
        }}
        onClose={() => {
          deleteDialog.hide();
          handleDialogClose();
        }}
      />
      <TableCellCustom align="center" onClick={handleDetails}>
        <TruncatedTypography variant="body2" text={row?.reasonOfDecline || 'NA'} width={120} />
      </TableCellCustom>
      <TableCellCustom align="center" onClick={handleDetails}>
        <StatusTag status={LeaveStatus[statusValue]} />
      </TableCellCustom>
      <TableCellCustom align="center" onClick={handleDetails}>
        {row?.approvedUser?.firstName || row?.approvedUser?.lastName ? (
          <Stack
            direction="row"
            sx={{ display: 'flex', alignItems: 'center', gap: '9px', width: '150px' }}
          >
            <CustomAvatar name={updatedBy} color="info" />
            <Typography variant="body2">{updatedBy}</Typography>
          </Stack>
        ) : (
          'NA'
        )}
      </TableCellCustom>

      {!isSuperAdmin && itSelf && (
        <TableCellCustom align="center">
          <RowActions row={row} itSelf={itSelf} />
        </TableCellCustom>
      )}
    </>
  );
};

export { LeaveRow };
